import React, { useState } from 'react';
import styled from 'styled-components';
import { Size } from '../../common/size';
import { Button } from '../Button/Button';
import { Icon, IconNames } from '../Icons/Icon';
import { Label } from '../../Typography/Text/Text';
import { useTestId } from '../../hooks/useTestId';
import { CardLarge } from '../Card/Card';
import { IThemePalette } from '../../Themes/interfaces';

export const carouselColours = (palette: IThemePalette) => ({
  carousel: {
    slickDot: {
      background: palette.primary.second[25],
      selected: {
        background: palette.primary.second[75]
      }
    },
    closeButton: {
      background: palette.primary.first[75]
    }
  }
});

export interface ICarouselProps {
  onClose: (e: React.MouseEvent<SVGElement>) => void;
  moveBackButtonText: string;
  moveForwardButtonText: string;
  finalSlideExitButtonText?: string;
  onFinalSlideExitButtonClick: () => void;
  onNextClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

interface ISlickDotProps {
  active: boolean;
}

export const NextButton = styled(Button)`
  width: 9.5rem;
`;

const SlickDotsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  > *:not(:last-child) {
    margin-right: 1rem;
  }
`;

const SlickDot = styled.div<ISlickDotProps>`
  width: 1rem;
  height: 1rem;
  border-radius: 50%;
  background-color: ${({ theme, active }) => (active ? theme.colours.carousel.slickDot.selected.background : theme.colours.carousel.slickDot.background)};
  cursor: pointer;
`;

const CarouselFooter = styled.div`
  box-sizing: border-box;
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  padding: 0 1.5rem 1.5rem 2rem;
`;

const FooterRight = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-left: auto;
  width: 60%;
`;

export const BackButton = styled(Label)`
  cursor: pointer;
`;

export const CloseButton = styled(Icon)`
  position: absolute;
  top: 1.5rem;
  left: 1.5rem;
  background-color: ${({ theme }) => theme.colours.carousel.closeButton.background};
  color: ${({ theme }) => theme.colours.default.background};
`;

export const CarouselItem = styled.div``;

enum SlideDirection {
  Backward = -1,
  Forward = 1
}

export const Carousel: React.FC<ICarouselProps> = ({
  children,
  moveBackButtonText,
  moveForwardButtonText,
  finalSlideExitButtonText = moveForwardButtonText,
  onFinalSlideExitButtonClick,
  onNextClick,
  onClose,
  ...rest
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const { generateTestId } = useTestId(rest, 'Carousel');

  const slides = React.Children.toArray(children).filter((item) => React.isValidElement<typeof CarouselItem>(item));

  const isFinalSlide = currentSlide + 1 === slides.length;

  const moveSlide = (direction: SlideDirection) => {
    if (isFinalSlide && direction == SlideDirection.Forward) {
      onFinalSlideExitButtonClick();
    } else {
      setCurrentSlide(currentSlide + direction);
    }
  };

  return (
    <CardLarge {...generateTestId()}>
      <CloseButton name={IconNames.X} size={Size.Medium} onClick={onClose} {...generateTestId('CloseButton')} />
      {slides[currentSlide]}
      <CarouselFooter {...generateTestId('Footer')}>
        {currentSlide !== 0 && (
          <BackButton onClick={() => moveSlide(SlideDirection.Backward)} {...generateTestId('BackButton')}>
            {moveBackButtonText}
          </BackButton>
        )}
        <FooterRight {...generateTestId('FooterRight')}>
          <SlickDotsContainer {...generateTestId('SlickDots')}>
            {slides.map((slide, index) => (
              <SlickDot active={currentSlide === index} key={index} onClick={() => setCurrentSlide(index)} {...generateTestId(`SlickDot${index}`)} />
            ))}
          </SlickDotsContainer>
          <NextButton
            label={isFinalSlide ? finalSlideExitButtonText : moveForwardButtonText}
            onClick={(e) => {
              moveSlide(SlideDirection.Forward);
              if (onNextClick) {
                onNextClick(e as React.MouseEvent<HTMLElement>);
              }
            }}
            {...generateTestId('NextButton')}
          />
        </FooterRight>
      </CarouselFooter>
    </CardLarge>
  );
};
